import React from 'react';
import { Autocomplete, Chip, TextField, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface ChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Values offered in the list; anything typed is still accepted. */
  suggestions?: string[];
  /** Returns a message when a value is not acceptable, else null. An invalid chip is kept, shown in error styling, with the message as its tooltip. */
  validate?: (value: string) => string | null;
  placeholder?: string;
  /** When false a comma is ordinary text, so values may contain commas; Enter, new lines in a paste and blur still end a value. Default true. */
  commaSeparates?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  'aria-label': string;
}

const clean = (raw: string): string => raw.trim();

/**
 * A list of short values as chips. Enter or a comma adds what is typed, a paste is split on commas and new lines, backspace on an empty box removes the
 * last chip, and the optional suggestions are picked from a list. Whatever is typed when the box loses focus
 * is kept, so a value is never lost for want of an Enter.
 */
export const ChipInput: React.FC<ChipInputProps> = ({
  value,
  onChange,
  suggestions,
  validate,
  placeholder,
  commaSeparates = true,
  disabled,
  sx,
  'aria-label': ariaLabel,
}) => {
  const [input, setInput] = React.useState('');

  const commit = (next: string[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of next) {
      const item = clean(raw);
      const key = item.toLowerCase();
      if (!item || seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    onChange(out);
  };

  return (
    <Autocomplete<string, true, false, true>
      multiple
      freeSolo
      autoSelect
      filterSelectedOptions
      size="small"
      disabled={disabled}
      // Above a Dialog, so the list opens over the modal it sits in.
      slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } } }}
      options={suggestions ?? []}
      value={value}
      inputValue={input}
      onInputChange={(_, next, reason) => {
        if (reason !== 'input') {
          setInput(next === '' || reason === 'clear' ? '' : next);
          return;
        }
        if (!commaSeparates || !next.includes(',')) {
          setInput(next);
          return;
        }
        // A comma ends a value: everything before the last comma is committed, the rest stays in the box.
        const parts = next.split(',');
        const rest = parts.pop() ?? '';
        commit([...value, ...parts]);
        setInput(rest.trimStart());
      }}
      onChange={(_, next) => {
        commit(next as string[]);
        setInput('');
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const { key, ...rest } = getTagProps({ index });
          const problem = validate ? validate(tag) : null;
          const chip = <Chip key={key} size="small" label={tag} {...rest} {...(problem ? { color: 'error' as const, variant: 'outlined' as const } : {})} />;
          return problem ? (
            <Tooltip key={key} title={problem}>
              <span>{chip}</span>
            </Tooltip>
          ) : chip;
        })
      }
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value.length === 0 ? placeholder : undefined}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-label': ariaLabel,
              onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
                const text = event.clipboardData.getData('text');
                if (!(commaSeparates ? /[,\n\r]/ : /[\n\r]/).test(text)) return;
                event.preventDefault();
                commit([...value, input, ...text.split(commaSeparates ? /[,\n\r]+/ : /[\n\r]+/)]);
                setInput('');
              },
            },
          }}
        />
      )}
    />
  );
};
